import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AppProvider, useAppData } from './context';
import { useAnalyticsScript } from './services/analytics/useAnalyticsScript';

import Helmet from './components/Helmet/Helmet';
import Loading from './pages/LoadingPage/LoadingPage';
import ProductPage from './pages/ProductPage/ProductPage';

import configData from './config.json';
import { initAppInsights } from './analytics/appInsights';
import { APP_PHASES } from './hooks/useAppInitialization/reducer';
import { useResolvedViewTracking } from './services/analytics/useResolvedViewTracking';
import { isRightToLeft } from './utils/helpers/functions.helper';

// These are not part of the expected primary rendering path.
const ErrorPage = lazy(() => import('./pages/ErrorPage/ErrorPage'));
const AssociatePage = lazy(() => import('./pages/AssociatePage/AssociatePage'));

function RouteChunkFallback() {
  return (
    <div className="route-chunk-loading" role="status" aria-label="Loading">
      <span className="route-chunk-spinner" aria-hidden="true" />
    </div>
  );
}

function RolexWorkflow({ rightToLeft }) {
  const {
    phase,
    loading,
    error,
    association,
    watchData,
    businessData,
    generatedCode,
    status,
    correlationId,
  } = useAppData();

  useResolvedViewTracking();

  // This is intentionally static: it is the initial state.
  if (loading) {
    return <Loading />;
  }

  // Error and associate flows are code-split because they are not expected
  // to be the primary product-render path.
  if (error) {
    return <ErrorPage errorStatusCode={status} correlationId={correlationId} />;
  }

  if (association) {
    return <AssociatePage generatedCode={generatedCode} />;
  }

  if (phase === APP_PHASES.PRODUCT) {
    // ProductPage is intentionally not lazy-loaded: it owns the primary
    // above-the-fold content and may contain the LCP element.
    return (
      <ProductPage
        rightToLeft={rightToLeft}
        watchData={watchData}
        businessData={businessData}
      />
    );
  }

  // Defensive fallback: should normally be unreachable.
  return <Loading />;
}

/**
 * Starts non-critical third-party work after the app stops loading.
 *
 * If analytics/CMP is legally required before any processing occurs, retain
 * the mandatory consent bootstrap separately and defer only optional tags.
 */
function DeferredServices() {
  const { loading } = useAppData();
  const [servicesEnabled, setServicesEnabled] = useState(false);

  useEffect(() => {
    if (loading) {
      return undefined;
    }

    const startServices = () => setServicesEnabled(true);

    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(startServices, {
        // Ensure telemetry eventually starts even on a busy device.
        timeout: 4000,
      });

      return () => window.cancelIdleCallback?.(idleId);
    }

    const timeoutId = window.setTimeout(startServices, 2000);
    return () => window.clearTimeout(timeoutId);
  }, [loading]);

  useEffect(() => {
    if (!servicesEnabled) {
      return;
    }

    try {
      initAppInsights();
    } catch {
      // Do not console.error here. Report through a safe internal fallback
      // when available; console errors lower Lighthouse Best Practices.
    }
  }, [servicesEnabled]);

  const handleAnalyticsScriptLoad = useCallback(() => {
    // Reserved integration point for consent-aware analytics initialization.
  }, []);

  const handleAnalyticsScriptError = useCallback(() => {
    // Avoid console.error for expected vendor/network failures.
    // If telemetry is already initialized, report a sanitized diagnostic there.
  }, []);

  /*
   * Assumption: this hook treats a falsy URL as "do not inject a script".
   * If it does not, update the hook to support an `enabled` option instead.
   */
  useAnalyticsScript(servicesEnabled ? configData.analyticsUrl : null, {
    onLoad: handleAnalyticsScriptLoad,
    onError: handleAnalyticsScriptError,
  });

  return null;
}

function Content() {
  const rightToLeft = useMemo(() => isRightToLeft(), []);

  return (
    <>
      <Helmet />

      <main
        id="main-content"
        tabIndex={-1}
        className={rightToLeft ? 'rtl' : undefined}
      >
        <Suspense fallback={<RouteChunkFallback />}>
          <RolexWorkflow rightToLeft={rightToLeft} />
        </Suspense>
      </main>
    </>
  );
}

function AppShell() {
  return (
    <>
      <DeferredServices />

      <Routes>
        <Route path="/" element={<Content />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppProvider>
        <AppShell />
      </AppProvider>
    </BrowserRouter>
  );
}

export default App;
